import { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { supabase } from '../lib/supabase'; // Assicurati di avere il client Supabase configurato
import React from 'react';
import { BUSINESS } from '../lib/business';
import { useSiteContent } from '../hooks/useSiteContent';

export default function Contact() {
  const { content: contacts } = useSiteContent('site.contacts');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null); // Reset error when user starts typing
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Salva il messaggio nel database Supabase
      const { error } = await supabase
        .from('contact_messages')
        .insert([
          {
            name: formData.name,
            email: formData.email,
            subject: formData.subject,
            message: formData.message,
            created_at: new Date().toISOString()
          }
        ]);

      if (error) {
        throw error;
      }

      // Opzione 1: Usa una Edge Function per inviare l'email
      const { error: emailError } = await supabase.functions.invoke('send-contact-email', {
        body: {
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message
        }
      });

      if (emailError) {
        console.error('Errore invio email:', emailError);
        // Non blocchiamo il successo se solo l'email fallisce
      }

      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });

    } catch (err: any) {
      console.error('Errore durante l\'invio:', err);
      setError('Si è verificato un errore durante l\'invio del messaggio. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  // Traduzione degli oggetti per il select
  const subjectOptions = [
    { value: 'order', label: 'Informazioni ordine' },
    { value: 'product', label: 'Informazioni prodotto' },
    { value: 'collaboration', label: 'Collaborazione' },
    { value: 'info', label: 'Informazioni generali' },
    { value: 'other', label: 'Altro' }
  ];

  return (
    <section className="container py-10">
      <h1 className="text-3xl font-bold text-foreground mb-6">
        Contattaci
      </h1>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Contact Information */}
        <div className="md:col-span-1">
          <div className="bg-card rounded-lg shadow-md p-6 h-full">
            <h2 className="text-xl font-semibold text-foreground mb-6">
              Informazioni di contatto
            </h2>
            
            <div className="space-y-6">
              {BUSINESS.addressLines.length > 0 && (
              <div className="flex items-start">
                <MapPin className="h-6 w-6 text-primary mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-medium text-foreground">
                    Dove siamo
                  </h3>
                  <p className="text-muted-foreground">
                    {BUSINESS.addressLines.map((line) => (
                      <React.Fragment key={line}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </p>
                </div>
              </div>
              )}
              {contacts.phone && (
              <div className="flex items-start">
                <Phone className="h-6 w-6 text-primary mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-medium text-foreground">
                    Telefono
                  </h3>
                  <p className="text-muted-foreground">
                    {contacts.phone}
                  </p>
                </div>
                </div>
                )}
              <div className="flex items-start">
                  <Mail className="h-6 w-6 text-primary mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-medium text-foreground">
                      Email
                    </h3>
                    <p className="text-muted-foreground">
                      Customer Service:<br />
                      {contacts.email}
                    </p>
                  </div>
                </div>
            </div>
          </div>
        </div>
        
        {/* Contact Form */}
        <div className="md:col-span-2">
          <div className="bg-card rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-foreground mb-6">
              Scrivici
            </h2>
            
            {error && (
              <div className="mb-6 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}
            
            {submitted ? (
              <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-6 text-center">
                <div className="flex justify-center mb-4">
                  <div className="bg-green-100 dark:bg-green-800 rounded-full p-3">
                    <Send className="h-8 w-8 text-green-600 dark:text-green-300" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  Messaggio inviato con successo!
                </h3>
                <p className="text-muted-foreground mb-4">
                  Grazie per averci contattato. Ti risponderemo il prima possibile.
                </p>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setError(null);
                  }}
                  className="px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                >
                  Invia un altro messaggio
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground/80 mb-1">
                      Nome *
                    </label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground/80 mb-1">
                      Email
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground/80 mb-1">
                    Oggetto *
                  </label>
                  <Select
                    name="subject"
                    value={formData.subject || undefined}
                    onValueChange={(value) => handleChange('subject', value)}
                    required
                  >
                    <SelectTrigger id="subject">
                      <SelectValue placeholder="Seleziona un argomento" />
                    </SelectTrigger>
                    <SelectContent>
                      {subjectOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground/80 mb-1">
                    Messaggio *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    required
                    rows={6}
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      Invia Messaggio
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}